import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Star } from "lucide-react";
import { API_URL, apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

export default function RatePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [score, setScore] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios
      .get(`${API_URL}/public/reviews/${token}`)
      .then(({ data }) => {
        setInfo(data);
        setDone(data.done);
      })
      .catch(() => setInfo({ error: true }));
  }, [token]);

  const submit = async () => {
    if (!score) return;
    try {
      await axios.post(`${API_URL}/public/reviews/${token}`, { score, comment });
      setDone(true);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6" data-testid="rate-page">
      <div className="w-full max-w-sm bg-white border border-[#E5E5E5] rounded-2xl p-8 text-center">
        {!info ? (
          <p className="text-neutral-400">Loading…</p>
        ) : info.error ? (
          <p className="text-neutral-500" data-testid="rate-invalid">This rating link is not valid.</p>
        ) : done ? (
          <div data-testid="rate-thanks">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h1 className="mt-4 text-2xl font-black">Thank you!</h1>
            <p className="mt-2 text-sm text-neutral-500">Your feedback has been shared with {info.business_name}.</p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-black">How did {info.business_name} do?</h1>
            <p className="mt-2 text-sm text-neutral-500">Tap a star to rate your experience.</p>
            <div className="mt-6 flex justify-center gap-2" data-testid="rate-stars">
              {[1, 2, 3, 4, 5].map((i) => (
                <button
                  key={i}
                  onClick={() => setScore(i)}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(0)}
                  data-testid={`rate-star-${i}`}
                  className="transition-transform duration-100 hover:scale-110"
                >
                  <Star className={`h-9 w-9 ${i <= (hover || score) ? "fill-amber-400 text-amber-400" : "text-neutral-300"}`} />
                </button>
              ))}
            </div>
            <Textarea
              rows={3}
              className="mt-6"
              placeholder="Anything you'd like to add? (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="rate-comment-input"
            />
            <Button onClick={submit} disabled={!score} className="mt-4 w-full rounded-full" data-testid="rate-submit-button">
              Submit rating
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
